import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, Card, CardContent, Chip, Stack, Tooltip, Typography } from '@mui/material';

import apiClient from '@src/api/clients';
import { toast } from '@src/components/core/toaster';

const STATUS_CHIPS = {
  done: { label: 'Done', color: 'success' },
  waiting_on_client: { label: 'Waiting on client', color: 'warning' },
  waiting_on_step: { label: 'Waiting', color: 'default' },
  ready: { label: 'Ready', color: 'info' },
  ran: { label: 'Started', color: 'info' },
  report_only: { label: 'Report only', color: 'default', variant: 'outlined' },
  failed: { label: 'Failed', color: 'error' },
  skipped: { label: 'Not needed', color: 'default', variant: 'outlined' },
};

const MODE_NOTE = {
  off: 'Runner is off — this shows what each step is waiting on; nothing runs.',
  report: 'Runner is in report mode — it records what would run and calls nothing.',
  apply: 'Runner is on — ready steps run every 20 minutes (each step’s own setting still applies).',
};

const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : '';

// The launch runner's steps for this client: brand profile through the paused
// campaign build. Staff only; "Run now" retries anything that failed.
export default function LaunchStatusCard({ clientSlug }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const url = `app1/clients/${clientSlug}/launch-status/`;

  const load = useCallback(() => {
    if (!clientSlug) return;
    apiClient
      .request({ url, method: 'GET' })
      .then((res) => setData(res?.data || { steps: [] }))
      .catch(() => setData({ steps: [], error: true }));
  }, [clientSlug, url]);

  useEffect(() => { load(); }, [load]);

  const runNow = async () => {
    setBusy(true);
    try {
      const res = await apiClient.request({ url, method: 'POST', payload: {} });
      toast.success(res?.data?.detail || 'Queued');
      if (res?.data?.steps) setData(res.data);
      setTimeout(load, 15000);
    } catch (err) {
      const body = err?.response?.data;
      toast.error(body?.detail || 'Could not start the runner');
      if (body?.steps) setData(body);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card variant="outlined" sx={{ mb: 2 }}>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={700}>Launch steps</Typography>
          <Button size="small" variant="outlined" onClick={runNow} disabled={busy || !data || data.mode === 'off'}>
            Run now
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
          {data ? MODE_NOTE[data.mode] || '' : ''}
        </Typography>
        {data === null && <Typography variant="body2" color="text.secondary">Loading…</Typography>}
        {data?.error && <Typography variant="body2" color="text.secondary">Could not load launch status.</Typography>}
        <Stack spacing={1}>
          {(data?.steps || []).map((s) => {
            const chip = STATUS_CHIPS[s.status] || { label: s.status, color: 'default' };
            const tip = [
              s.at && `Updated ${fmtDate(s.at)}`,
              s.attempts > 0 && `${s.attempts} failed attempt${s.attempts === 1 ? '' : 's'}`,
              s.next_attempt_at && `Next try ${fmtDate(s.next_attempt_at)}`,
              s.escalated_at && `Team told ${fmtDate(s.escalated_at)}`,
            ].filter(Boolean).join(' · ');
            return (
              <Box key={s.key} sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ lineHeight: 1.35 }}>{s.label}</Typography>
                  {s.message && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', wordBreak: 'break-word' }}>
                      {s.message}
                    </Typography>
                  )}
                </Box>
                <Tooltip title={tip} disableHoverListener={!tip}>
                  <Chip size="small" label={chip.label} color={chip.color} variant={chip.variant || 'filled'} />
                </Tooltip>
              </Box>
            );
          })}
        </Stack>
      </CardContent>
    </Card>
  );
}

LaunchStatusCard.propTypes = {
  clientSlug: PropTypes.string,
};
